import { getConfig } from "@/lib/config";
import { csrfToken, setCsrfToken } from "@/auth/csrf";
import { loginUrlFor } from "@/auth/login-url";
import { useAuthStore } from "@/stores/auth-store";

// Imperative auth side-effects the UI can call: redirect-to-login,
// logout. /auth/refresh is handled inline by the BFF's GraphQL proxy
// (see apps/bff/src/routes/graphql.ts) so the SPA rarely needs to
// call it manually.

const LOGOUT_TIMEOUT_MS = 5_000;

export function redirectToLogin(next?: string): void {
  const target = next ?? window.location.pathname + window.location.search;
  window.location.assign(loginUrlFor(target));
}

// `next` is where the user lands after the NEXT successful login. It defaults
// to home, which is what an intentional logout wants. The session-expiry notice
// passes the page the user was on: there the logout is not the goal, only the
// teardown of a dead session before signing in again (#177).
export async function logout(next = "/"): Promise<void> {
  const cfg = getConfig();
  const token = csrfToken();
  try {
    // No CSRF token means no session; there is nothing to tear down server-side.
    if (token) {
      await fetch(`${cfg.basePath}auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "x-csrf-token": token },
        // Bounded on purpose: the teardown must never be what keeps a user on a
        // page they are done with. Account deletion is the sharp case (#242) -
        // there the session is already revoked server-side, so this call talks
        // to something that is gone and must not decide whether we leave.
        signal: AbortSignal.timeout(LOGOUT_TIMEOUT_MS),
      });
    }
  } catch {
    // Even if the BFF call fails or times out, clear local state - the worst
    // case is a stranded refresh token at Keycloak, not a security issue.
  }
  // Reached on every path: callers may treat "logout() returned" as "the
  // session is gone and we are leaving for the login screen".
  setCsrfToken(null);
  useAuthStore.getState().setAnonymous();
  redirectToLogin(next);
}
